import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import type { Json } from '@/integrations/supabase/types'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog'
import { Progress } from '@/components/ui/progress'
import { GenericBatchAnalysisModule } from '@/components/GenericBatchAnalysisModule'
import { MODULES_ORDERED, getModule, getModuleByLegacyId, mapLegacyToNewModuleId, getModuleAliases, type ModuleDefinition, type DataGateStatus, type FileKey, type EngagementFile, type AuditFindingExtended, type MaterialityThresholds } from '@/lib/modules'
import { copyRevisionFindingsToAudit, extractDaysFromFinding } from '@/lib/modules/findings-utils'
import { SieAccountingBasisCard } from '@/components/SieAccountingBasisCard'
import { normalizeTraceability, type DataReference } from '@/lib/modules/traceability-utils'

import { ModuleAnalysesTab } from '@/components/ModuleAnalysesTab'
import { ModuleOverviewTab, ModuleFilesTab, ModuleRunTab, ModuleResultsTab, ModuleReportTab } from '@/components/module-tabs'
import { MaterialityThresholdsPanel } from '@/components/MaterialityThresholdsPanel'
import { DeletedFilesPanel } from '@/components/DeletedFilesPanel'
import { useBackendWorkerAnalysis } from '@/hooks/useBackendWorkerAnalysis'
import { 
  ArrowLeft, 
  FileText, 
  Brain, 
  MessageSquare, 
  Calendar,
  User,
  AlertCircle,
  CheckCircle,
  Play,
  Download,
  Upload,
  Plus,
  Edit,
  Trash2,
  Clock,
  Target,
  TrendingUp,
  X,
  Link,
  RefreshCw,
  Eye,
  AlertTriangle,
  BarChart3,
  Settings,
  ChevronDown,
  FolderOpen
} from 'lucide-react'
import { useActiveCases } from '@/hooks/useActiveCases'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useToast } from '@/hooks/use-toast'
import { MembersManager } from '@/components/MembersManager'
import { CreateClientUploadLinkModal } from '@/components/CreateClientUploadLinkModal'
import { ClientUploadLinksTable, ClientUploadToken } from '@/components/ClientUploadLinksTable'
import { toast } from '@/hooks/use-toast'
import { useUserRole } from '@/hooks/useUserRole'
import { useTranslation } from 'react-i18next'
import { formatWithUTC } from '@/lib/datetime'

// NOTE: Module type aliases are now centralized in @/lib/modules/module-registry.ts
// Use getModuleAliases() from the import above

interface Audit {
  id: string
  title: string
  description: string
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'initial_review' | 'detailed_analysis' | 'error'
  created_at: string
  updated_at: string
  user_id: string
  data_input: any
}

interface Document {
  id: string
  filename: string
  original_filename: string
  size_bytes: number
  created_at: string
  module_type?: string
  storage_path: string
  mime_type: string
  uploaded_by: string
  audit_id: string
  updated_at: string
  file_key?: string
}

interface Analysis {
  id: string
  type: 'initial' | 'detailed' | 'compliance'
  status: 'pending' | 'running' | 'completed' | 'failed'
  results?: any
  created_at: string
  completed_at?: string
}

interface Comment {
  id: string
  content: string
  author: string
  created_at: string
}

interface ActionItem {
  id: string
  title: string
  description: string
  priority: 'low' | 'medium' | 'high' | 'critical'
  assigned_to?: string
  due_date?: string
  status: 'open' | 'in_progress' | 'completed'
  created_at: string
  module_id?: string
}

interface Finding {
  id: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  title: string
  description: string
  evidence: string
  responsible: string
  status: 'open' | 'in_progress' | 'resolved'
  updated_at: string
  module_id: string
}

interface AuditFindingLite {
  id: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  finding_type: string
  description: string
  created_at: string
  data_reference?: Record<string, any>
  module_id?: string
}

interface FindingDecision {
  id: string
  finding_id: string
  decision_type: 'accepted' | 'needs_evidence' | 'dismissed'
  comment: string
  user_id: string
  user_role: string
  created_at: string
}

interface FindingComment {
  id: string
  finding_id: string
  content: string
  user_id: string
  user_name: string
  mentions: string[]
  attachments?: any
  created_at: string
}

interface ModuleSignoff {
  id: string
  audit_id: string
  module_id: string
  signed_off_by: string
  signed_off_at: string
  comment?: string
}

interface AuditSignoff {
  id: string
  audit_id: string
  signed_off_by: string
  signed_off_at: string
  comment?: string
}

interface Organization {
  id: string
  name: string
}

interface UserProfile {
  id: string
  full_name?: string
}

interface AuditMember {
  id: string
  user_id: string
  role: string
  added_at: string
  full_name?: string
  email?: string
  avatar_url?: string
}

interface CaseDetailPageProps {
  organization: Organization | null
  profile: UserProfile | null
}

const statusColors = {
  pending: 'bg-warning/10 text-warning border-warning/20',
  processing: 'bg-primary/10 text-primary border-primary/20',
  completed: 'bg-success/10 text-success border-success/20',
  failed: 'bg-destructive/10 text-destructive border-destructive/20',
  initial_review: 'bg-secondary/10 text-secondary border-secondary/20',
  detailed_analysis: 'bg-primary/10 text-primary border-primary/20',
  error: 'bg-destructive/10 text-destructive border-destructive/20'
}

const priorityColors = {
  low: 'bg-muted text-muted-foreground border-muted',
  medium: 'bg-warning/10 text-warning border-warning/20',
  high: 'bg-destructive/10 text-destructive border-destructive/20',
  critical: 'bg-destructive text-destructive-foreground border-destructive'
}

// Use the new module registry (8 modules: 0-7)
const modules = MODULES_ORDERED.map(m => ({
  id: m.id,
  nameKey: m.nameKey,
  displayOrder: m.displayOrder,
  isDataGate: m.isDataGate,
  legacyIds: m.legacyIds,
  requiredFiles: m.requiredFiles,
  optionalFiles: m.optionalFiles,
}))

const moduleStatusLabels = {
  'not_started': 'Ej startad',
  'in_progress': 'Pågår', 
  'completed': 'Klart',
  'excluded': 'Exkluderad'
}

const moduleStatusColors = {
  'not_started': 'bg-muted text-muted-foreground border-muted',
  'in_progress': 'bg-warning/10 text-warning border-warning/20',
  'completed': 'bg-success/10 text-success border-success/20',
  'excluded': 'bg-secondary/10 text-secondary border-secondary/20'
}

export function CaseDetailPage({ organization, profile }: CaseDetailPageProps) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { toast } = useToast()
  const { role } = useUserRole()
  const { t } = useTranslation()
  
  // Get active cases for dropdown
  const { cases: activeCases } = useActiveCases()
  
  const [audit, setAudit] = useState<Audit | null>(null)
  const [documents, setDocuments] = useState<Document[]>([])
  const [analyses, setAnalyses] = useState<Analysis[]>([])
  const [comments, setComments] = useState<Comment[]>([])
  const [actionItems, setActionItems] = useState<ActionItem[]>([])
  const [loading, setLoading] = useState(true)
  const [newComment, setNewComment] = useState('')
  const [selectedModule, setSelectedModule] = useState<string | null>(null)
  const [selectedModuleTab, setSelectedModuleTab] = useState('overview')
  const [findings, setFindings] = useState<Finding[]>([])
  const [moduleStates, setModuleStates] = useState<Record<string, { status: keyof typeof moduleStatusLabels, documentsUploaded: number, documentsRequired: number }>>({})
  const [auditFindings, setAuditFindings] = useState<AuditFindingLite[]>([])
  const [engagementFiles, setEngagementFiles] = useState<Record<FileKey, EngagementFile | undefined>>({} as Record<FileKey, EngagementFile | undefined>)
  const [dataGateStatus, setDataGateStatus] = useState<DataGateStatus>('not_run')
  const [isRunningAnalysis, setIsRunningAnalysis] = useState(false)
  const [moduleFindingsExtended, setModuleFindingsExtended] = useState<AuditFindingExtended[]>([])
  
  // Analysis summary (per audit) derived from audit_findings
  const [analysisCount, setAnalysisCount] = useState<number>(0)
  const [analysisSeverity, setAnalysisSeverity] = useState<{ high: number; medium: number; low: number }>({ high: 0, medium: 0, low: 0 })
  const [rawFindings, setRawFindings] = useState<any[]>([])
  
  const [findingDecisions, setFindingDecisions] = useState<FindingDecision[]>([])
  const [selectedFinding, setSelectedFinding] = useState<string | null>(null)
  const [decisionComment, setDecisionComment] = useState('')
  const [newFindingComment, setNewFindingComment] = useState<Record<string, string>>({})
  const [findingComments, setFindingComments] = useState<Record<string, FindingComment[]>>({})
  const [moduleSignoffs, setModuleSignoffs] = useState<ModuleSignoff[]>([])
  const [auditSignoff, setAuditSignoff] = useState<AuditSignoff | null>(null)
  const [members, setMembers] = useState<AuditMember[]>([])
  const [isOwner, setIsOwner] = useState(false)

  // Report generation states
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [hasExistingReport, setHasExistingReport] = useState(false);
  const [showClientUploadModal, setShowClientUploadModal] = useState(false);
  const [clientUploadTokens, setClientUploadTokens] = useState<ClientUploadToken[]>([]);

  // Dashboard states for module overview
  const [moduleStats, setModuleStats] = useState<{
    totalFindings: number
    criticalFindings: number
    highFindings: number
    mediumFindings: number
    lowFindings: number
    analysesRun: number
    totalRows: number
    lastAnalysisDate: string | null
  } | null>(null)
  const [latestFindings, setLatestFindings] = useState<any[]>([])
  const [taxAccountMetrics, setTaxAccountMetrics] = useState<{
    negativeBalanceDays: number
    totalDeviationAmount: number
    latePayments: number
  } | null>(null)

  // Tax account configuration state
  const [toleranceDays, setToleranceDays] = useState(0)
  const [companySize, setCompanySize] = useState<'small' | 'large'>('small')
  const [vatReportingPeriod, setVatReportingPeriod] = useState<'monthly' | 'quarterly'>('monthly')
  
  // Materiality thresholds state
  const [materialityThresholds, setMaterialityThresholds] = useState<MaterialityThresholds>({})
  const [isSavingThresholds, setIsSavingThresholds] = useState(false)

  // Sync selectedModule with URL query param
  useEffect(() => {
    const moduleFromUrl = searchParams.get('module')
    if (moduleFromUrl && MODULES_ORDERED.some(m => m.id === moduleFromUrl)) {
      setSelectedModule(moduleFromUrl)
    }
  }, [searchParams])

  // Update URL when module changes
  const handleModuleSelect = useCallback((moduleId: string | null) => {
    setSelectedModule(moduleId)
    if (moduleId) {
      setSearchParams({ module: moduleId })
    } else {
      setSearchParams({})
    }
  }, [setSearchParams])

  // Backend worker analysis hook for tax account module (module_5)
  const isTaxAccountModule = selectedModule === 'module_5' || selectedModule === 'taxes_fees';
  
  const backendWorker = useBackendWorkerAnalysis({
    auditId: id || '',
    moduleType: selectedModule || 'taxes_fees',
    onComplete: async (revisionId) => {
      console.log('[CaseDetailPage] Backend worker analysis complete, revisionId:', revisionId);
      
      // Copy findings to audit_findings table for the Actions tab
      if (revisionId && id && selectedModule) {
        try {
          const result = await copyRevisionFindingsToAudit(revisionId, id, selectedModule, selectedModule);
          console.log(`[CaseDetailPage] Copied ${result.copiedCount} findings to audit_findings`);
        } catch (copyErr) {
          console.warn('[CaseDetailPage] Failed to copy findings:', copyErr);
        }
      }
      
      await fetchCaseData();
      await fetchModuleStats();
      await fetchLatestFindings();
      await fetchModuleFindingsExtended();
      setSelectedModuleTab('results');
    },
    onError: (error) => {
      console.error('[CaseDetailPage] Backend worker error:', error);
    },
  });

  useEffect(() => {
    if (id) {
      fetchCaseData()
    }
  }, [id])

  // Auto-copy findings from completed revisions if missing in audit_findings
  useEffect(() => {
    if (!id || !selectedModule) return;
    
    const ensureFindingsAreCopied = async () => {
      // Get latest completed revision for this module
      // Get all module aliases for backward compatibility
      const moduleAliases = getModuleAliases(selectedModule)
      const moduleTypeFilter = moduleAliases.map(a => `module_type.eq.${a}`).join(',')
      
      const { data: latestRevision } = await supabase
        .from('revisions')
        .select('id, status')
        .eq('audit_id', id)
        .or(moduleTypeFilter)
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
      
      if (!latestRevision) return;
      
      // Check if findings for this revision already exist in audit_findings
      const { count } = await supabase
        .from('audit_findings')
        .select('id', { count: 'exact', head: true })
        .eq('audit_id', id)
        .eq('run_id', latestRevision.id);
      
      // If no findings exist for this revision, copy them
      if (count === 0) {
        console.log('[CaseDetailPage] Auto-copying missing findings for revision:', latestRevision.id);
        try {
          const { copyRevisionFindingsToAudit } = await import('@/lib/modules/findings-utils');
          const result = await copyRevisionFindingsToAudit(latestRevision.id, id, selectedModule, selectedModule);
          console.log(`[CaseDetailPage] Auto-copied ${result.copiedCount} findings`);
          if (result.copiedCount > 0) {
            await fetchCaseData();
          }
        } catch (err) {
          console.warn('[CaseDetailPage] Failed to auto-copy findings:', err);
        }
      }
    };
    
    ensureFindingsAreCopied();
  }, [id, selectedModule]);

  // Display findings directly from database - no client-side filtering
  // Tolerance filtering is applied at analysis time in audit-tax-account-analyze edge function
  useEffect(() => {
    if (rawFindings.length === 0) {
      setAnalysisCount(0);
      setAnalysisSeverity({ high: 0, medium: 0, low: 0 });
      setAuditFindings([]);
      return;
    }
    
    // Database already contains filtered findings - just display them
    const findingsWithModule = rawFindings.map((f: any) => ({
      ...f,
      module_id: f.module_id || 'unknown'
    }));
    
    setAuditFindings(findingsWithModule);
    setAnalysisCount(rawFindings.length);
    setAnalysisSeverity({
      high: rawFindings.filter((f: any) => f.severity === 'high' || f.severity === 'critical').length,
      medium: rawFindings.filter((f: any) => f.severity === 'medium' || f.severity === 'warning').length,
      low: rawFindings.filter((f: any) => f.severity === 'low' || f.severity === 'info').length,
    });
  }, [rawFindings]); // Only depends on rawFindings - tolerance is a run-time parameter

  // Realtime subscription for audit_documents changes
  useEffect(() => {
    if (!id) return;

    const channel = supabase
      .channel(`audit-documents-${id}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen for INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'audit_documents',
          filter: `audit_id=eq.${id}`,
        },
        (payload) => {
          console.log('📡 Realtime document change:', payload.eventType, payload);
          // Refetch all data to ensure consistency
          fetchCaseData();
        }
      )
      .subscribe((status) => {
        console.log('📡 Realtime subscription status:', status);
      });

    return () => {
      console.log('📡 Unsubscribing from realtime channel');
      supabase.removeChannel(channel);
    };
  }, [id])

  // Function to recalculate module states when documents change
  const recalculateModuleStates = useCallback((docs: Document[], signoffs: ModuleSignoff[]) => {
    console.log('🔄 Recalculating module states with docs:', docs, 'signoffs:', signoffs);
    
    const moduleStatesTemp: Record<string, { status: keyof typeof moduleStatusLabels, documentsUploaded: number, documentsRequired: number }> = {}
    
    modules.forEach(module => {
      const moduleDocuments = docs?.filter(doc => doc.module_type === module.id) || []
      // Count unique file_keys instead of total documents to avoid counting duplicates
      const uniqueFileKeys = new Set(moduleDocuments.map(doc => doc.file_key).filter(Boolean))
      const uploaded = uniqueFileKeys.size
      const required = module.requiredFiles?.length || 1
      
      // Check if module is signed off
      const isSignedOff = signoffs.some(signoff => signoff.module_id === module.id)
      
      console.log(`📊 Module ${module.id}: ${uploaded} documents, signed off: ${isSignedOff}`, moduleDocuments);
      
      let status: keyof typeof moduleStatusLabels = 'not_started'
      if (isSignedOff) {
        status = 'completed'
      } else if (uploaded > 0) {
        status = 'in_progress'
      }
      
      moduleStatesTemp[module.id] = {
        status,
        documentsUploaded: uploaded,
        documentsRequired: required
      }
    })
    
    console.log('📊 Final module states:', moduleStatesTemp);
    setModuleStates(moduleStatesTemp)
  }, [modules])

  // Fetch module-specific findings for the Actions tab
  const fetchModuleFindingsExtended = useCallback(async () => {
    if (!id || !selectedModule) return;
    
    try {
      // Get all module aliases for backward compatibility
      const moduleAliases = getModuleAliases(selectedModule)
      const moduleIdFilter = moduleAliases.map(a => `module_id.eq.${a}`).join(',')
      
      const { data, error } = await supabase
        .from('audit_findings')
        .select('*')
        .eq('audit_id', id)
        .or(`${moduleIdFilter},module_id.is.null`)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('[fetchModuleFindingsExtended] Error:', error);
        return;
      }
      
      // Map to AuditFindingExtended format
      const extended: AuditFindingExtended[] = (data || []).map((f: any) => ({
        id: f.id,
        auditId: f.audit_id,
        moduleId: f.module_id || selectedModule,
        runId: f.run_id,
        severity: f.severity,
        title: f.title || f.finding_type,
        findingType: f.finding_type,
        description: f.description,
        status: f.status || 'open',
        flaggedByModel: f.flagged_by_model || 'batch-analysis',
        dataReference: f.data_reference,
        createdAt: f.created_at,
      }));
      
      console.log(`[fetchModuleFindingsExtended] Fetched ${extended.length} findings for module ${selectedModule}`);
      setModuleFindingsExtended(extended);
    } catch (err) {
      console.error('[fetchModuleFindingsExtended] Unexpected error:', err);
    }
  }, [id, selectedModule]);

  // Fetch dashboard stats for module overview
  useEffect(() => {
    if (selectedModule && id) {
      fetchModuleStats()
      fetchLatestFindings()
      fetchModuleFindingsExtended()
      checkExistingReport()
      // Check for tax account module (module_5 or legacy taxes_fees)
      if (selectedModule === 'module_5' || selectedModule === 'taxes_fees') {
        fetchTaxAccountMetrics()
      }
    }
  }, [selectedModule, id])

  // Check if a report already exists for the current module
  const checkExistingReport = async () => {
    if (!id || !selectedModule) return;
    
    try {
      // Get all module aliases for backward compatibility
      const moduleAliases = getModuleAliases(selectedModule)
      const moduleTypeFilter = moduleAliases.map(a => `module_type.eq.${a}`).join(',')
      
      const { data: revisions } = await supabase
        .from('revisions')
        .select('id')
        .eq('audit_id', id)
        .or(moduleTypeFilter)
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (!revisions || revisions.length === 0) {
        setHasExistingReport(false);
        return;
      }
      
      const { data: report } = await supabase
        .from('revision_reports')
        .select('id')
        .eq('revision_id', revisions[0].id)
        .maybeSingle();
      
      setHasExistingReport(!!report);
    } catch (err) {
      console.error('[checkExistingReport] Error:', err);
    }
  };

  // Helper to calculate total negative days from runs
  const calculateTotalNegativeDays = (runs: any[]): number => {
    if (!runs || runs.length === 0) return 0;
    return runs.reduce((sum, r) => sum + (r.days || 0), 0);
  };

  // Filter findings based on tolerance days
  // Only late_payment findings with days <= toleranceDays are filtered out
  const getFilteredFindings = useCallback((findings: AuditFindingExtended[]): AuditFindingExtended[] => {
    return findings.filter(f => {
      // Use shared helper - pass findingType for compatibility
      const days = extractDaysFromFinding({
        findingType: f.findingType,
        finding_type: f.findingType,
        description: f.description
      });
      // If we can't extract days or it's not a late payment, keep the finding
      if (days === null) return true;
      // Keep findings where days exceed tolerance
      return days > toleranceDays;
    });
  }, [toleranceDays]);

  // Generate report and navigate to Report tab
  const handleGenerateReport = async () => {
    // Apply tolerance filter to findings
    const originalFindingsCount = moduleFindingsExtended.length;
    const filteredFindings = getFilteredFindings(moduleFindingsExtended);
    const filteredAwayCount = originalFindingsCount - filteredFindings.length;
    
    // Mjuk varning: visa info om fynd filtrerades bort av toleransinställning
    if (filteredAwayCount > 0 && filteredFindings.length === 0) {
      toast({
        title: 'Toleransfiltrering aktiv',
        description: `${filteredAwayCount} fynd filtrerades bort pga toleransinställning (${toleranceDays} dagar). Rapporten genereras som "ren" med analysomfattning.`,
      });
    }
    
    // Only block if missing required params - NOT if 0 findings (clean report is OK)
    if (!id || !selectedModule) {
      toast({
        title: 'Fel',
        description: 'Ärende eller modul saknas',
        variant: 'destructive',
      });
      return;
    }
    
    setIsGeneratingReport(true);
    try {
      // Get latest revision (check all module aliases)
      const moduleAliases = getModuleAliases(selectedModule)
      const moduleTypeFilter = moduleAliases.map(a => `module_type.eq.${a}`).join(',')
      
      const { data: revisions } = await supabase
        .from('revisions')
        .select('id')
        .eq('audit_id', id)
        .or(moduleTypeFilter)
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(1);
      
      const revisionId = revisions?.[0]?.id;
      
      // Must have a completed revision to generate report
      if (!revisionId) {
        toast({
          title: 'Ingen analys genomförd',
          description: 'Kör en analys först för att kunna generera rapport',
          variant: 'destructive',
        });
        setIsGeneratingReport(false);
        return;
      }
      
      // Fetch rich data_summary and positive_assessments from aggregates (preferred) or batches (fallback)
      let dataSummary: any = null;
      let positiveAssessments: any[] = [];
      
      // Try aggregates first (canonical source)
      const { data: aggregates } = await supabase
        .from('aggregates')
        .select('summary_json')
        .eq('revision_id', revisionId)
        .maybeSingle();
      
      if (aggregates?.summary_json) {
        const summaryJson = aggregates.summary_json as any;
        dataSummary = summaryJson.data_summary || null;
        positiveAssessments = summaryJson.positive_assessments || [];
      }
      
      // Fallback to batches if aggregates didn't have data
      let batchDataSummary: any = null;
      if (!dataSummary) {
        const { data: batches } = await supabase
          .from('batches')
          .select('result_json')
          .eq('revision_id', revisionId)
          .eq('state', 'completed')
          .order('created_at', { ascending: false })
          .limit(1);
        
        const resultJson = batches?.[0]?.result_json as any;
        batchDataSummary = resultJson?.data_summary || null;
        dataSummary = batchDataSummary;
        positiveAssessments = resultJson?.positive_assessments || [];
      } else {
        batchDataSummary = dataSummary;
      }
      
      // Mjuk varning om dataSummary saknas (rapporten blir sämre)
      if (!dataSummary && filteredFindings.length === 0) {
        console.warn('[CaseDetailPage] Clean report without dataSummary - metadata will be limited');
        toast({
          title: 'Begränsad metadata',
          description: 'Analysmetadata (datumspann, radantal) kunde inte hämtas. Rapporten genereras ändå.',
          variant: 'default',
        });
      }
      
      console.log('[CaseDetailPage] Report generation payload:', {
        findingsCount: filteredFindings.length,
        originalFindingsCount,
        filteredAwayCount,
        hasDataSummary: !!dataSummary,
        positiveAssessmentsCount: positiveAssessments.length,
        revisionId,
      });

      // Build rich summary with negative balance statistics
      const negativeBalanceStats = batchDataSummary ? {
        totalDays: batchDataSummary.negative_days_count || calculateTotalNegativeDays(batchDataSummary.negative_balance_runs),
        occurrences: batchDataSummary.negative_balance_runs?.length || 0,
        maxConsecutiveDays: batchDataSummary.max_consecutive_negative_days || 0,
        minNegativeBalance: batchDataSummary.min_negative_balance,
        maxNegativeBalance: batchDataSummary.max_negative_balance,
        avgNegativeBalance: batchDataSummary.avg_negative_balance,
        // Legacy fallback fields
        minBalance: batchDataSummary.min_negative_balance ?? batchDataSummary.min_balance,
        maxBalance: batchDataSummary.max_negative_balance,
        runs: batchDataSummary.negative_balance_runs || [],
      } : null;
      
      // Map findings with normalized traceability data
      const mappedFindings = filteredFindings.map(f => {
        const trace = normalizeTraceability(f.dataReference as DataReference | null);
        return {
          severity: f.severity,
          category: f.findingType,
          message: f.description,
          details: f.dataReference?.details || f.recommendedNextStep || '',
          affectedPeriod: f.dataReference?.period || '',
          amount: f.dataReference?.amount || 0,
          score: f.dataReference?.score,
          // Use normalized traceability (canonical → affected_transactions → legacy)
          rowNumbers: trace.rowIndices,
          dates: trace.dates,
          amounts: trace.amounts,
          affectedTransactions: trace.affectedTransactions,
          positionNote: trace.positionNote,
        };
      });

      console.log('[CaseDetailPage] Mapped findings for report:', {
        totalFindings: mappedFindings.length,
        withRowNumbers: mappedFindings.filter(f => f.rowNumbers.length > 0).length,
        withDates: mappedFindings.filter(f => f.dates.length > 0).length,
        sampleFinding: mappedFindings[0],
      });

      const { data, error } = await supabase.functions.invoke('generate-module-report', {
        body: {
          findings: mappedFindings,
          summary: {
            total_findings: filteredFindings.length,
            original_findings: originalFindingsCount,
            filtered_away: filteredAwayCount,
            high_risk: filteredFindings.filter(f => f.severity === 'high' || f.severity === 'critical').length,
            medium_risk: filteredFindings.filter(f => f.severity === 'medium').length,
            low_risk: filteredFindings.filter(f => f.severity === 'low').length,
            // Add rich negative balance statistics
            negativeBalanceStats,
            interestTotal: batchDataSummary?.interest_total || 0,
            lateFeeDetected: batchDataSummary?.late_fee_detected || false,
            costInterestTotal: batchDataSummary?.cost_interest_total || 0,
            toleranceDays: toleranceDays, // Include tolerance setting in report
          },
          moduleType: selectedModule,
          companyName: audit?.title || null,
          revisionId: revisionId,
          // Include dataSummary and positiveAssessments for clean reports (0 findings)
          dataSummary: dataSummary,
          positiveAssessments: positiveAssessments,
        }
      });
      
      if (error) throw error;
      
      setHasExistingReport(true);
      toast({
        title: 'Rapport genererad',
        description: 'Rapporten är nu sparad och tillgänglig',
      });
    } catch (err) {
      console.error('[handleGenerateReport] Error:', err);
      toast({
        title: 'Fel vid rapportgenerering',
        description: err instanceof Error ? err.message : 'Kunde inte generera rapport',
        variant: 'destructive',
      });
    } finally {
      setIsGeneratingReport(false);
    }
  };

  /**
   * SINGLE SOURCE OF TRUTH: Fetch module stats from audit_findings table
   * This replaces the previous approach of reading from aggregates.summary_json
   */
  const fetchModuleStats = async () => {
    if (!id || !selectedModule) return
    
    try {
      // Get all module aliases for backward compatibility
      const moduleAliases = getModuleAliases(selectedModule)
      const moduleTypeFilter = moduleAliases.map(a => `module_type.eq.${a}`).join(',')
      
      // Fetch only the LATEST revision for this module (check all aliases)
      const { data: revisions } = await supabase
        .from('revisions')
        .select('id, created_at')
        .eq('audit_id', id)
        .or(moduleTypeFilter)
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(1)

      if (!revisions || revisions.length === 0) {
        setModuleStats(null)
        return
      }

      const latestRevision = revisions[0]

      // SINGLE SOURCE OF TRUTH: Fetch from audit_findings table
      // Include null module_id (legacy findings) and all module aliases
      const moduleIdFilter = moduleAliases.map(a => `module_id.eq.${a}`).join(',')
      const { data: findingsData, error: findingsError } = await supabase
        .from('audit_findings')
        .select('severity')
        .eq('audit_id', id)
        .or(`${moduleIdFilter},module_id.is.null`)

      if (findingsError) {
        console.error('[fetchModuleStats] Error fetching findings:', findingsError)
        setModuleStats(null)
        return
      }

      const findings = findingsData || []
      let criticalCount = 0
      let highCount = 0
      let mediumCount = 0
      let lowCount = 0

      findings.forEach((f: any) => {
        if (f.severity === 'critical') criticalCount++
        else if (f.severity === 'high') highCount++
        else if (f.severity === 'medium') mediumCount++
        else if (f.severity === 'low') lowCount++
      })

      // Get total rows from aggregate (this is metadata, not findings)
      const { data: aggregate } = await supabase
        .from('aggregates')
        .select('summary_json')
        .eq('revision_id', latestRevision.id)
        .single()
      
      const totalRows = (aggregate?.summary_json as any)?.totalRows || 
                        (aggregate?.summary_json as any)?.data_summary?.row_count || 0

      // Show only latest run (1) since all KPIs are based on latest revision
      setModuleStats({
        totalFindings: criticalCount + highCount + mediumCount + lowCount,
        criticalFindings: criticalCount,
        highFindings: highCount,
        mediumFindings: mediumCount,
        lowFindings: lowCount,
        analysesRun: 1,
        totalRows,
        lastAnalysisDate: latestRevision.created_at
      })
      
      console.log('[fetchModuleStats] SSOT: Loaded stats from audit_findings:', {
        total: findings.length,
        critical: criticalCount,
        high: highCount,
        medium: mediumCount,
        low: lowCount
      })
    } catch (err) {
      console.error('Error fetching module stats:', err)
    }
  }

  /**
   * SINGLE SOURCE OF TRUTH: Fetch latest findings from audit_findings table
   */
  const fetchLatestFindings = async () => {
    if (!id || !selectedModule) return
    
    try {
      // SINGLE SOURCE OF TRUTH: Fetch from audit_findings table (check all module aliases)
      const moduleAliases = getModuleAliases(selectedModule)
      const moduleIdFilter = moduleAliases.map(a => `module_id.eq.${a}`).join(',')
      
      const { data: findingsData, error } = await supabase
        .from('audit_findings')
        .select('*')
        .eq('audit_id', id)
        .or(`${moduleIdFilter},module_id.is.null`)
        .in('severity', ['critical', 'high'])
        .order('created_at', { ascending: false })
        .limit(3)

      if (error) {
        console.error('[fetchLatestFindings] Error:', error)
        setLatestFindings([])
        return
      }

      const criticalHighFindings = (findingsData || []).map((f: any) => ({
        id: f.id,
        severity: f.severity,
        findingType: f.finding_type,
        message: f.description,
        created_at: f.created_at
      }))

      console.log('[fetchLatestFindings] SSOT: Loaded from audit_findings:', criticalHighFindings.length)
      setLatestFindings(criticalHighFindings)
    } catch (err) {
      console.error('Error fetching latest findings:', err)
      setLatestFindings([])
    }
  }

  const fetchTaxAccountMetrics = async () => {
    if (!id || !selectedModule) return
    
    try {
      // Build filter for module type with aliases for backward compatibility
      const taxAccountAliases = getModuleAliases('taxes_fees');
      const moduleFilter = taxAccountAliases.map(a => `module_type.eq.${a}`).join(',');
      console.log(`[fetchTaxAccountMetrics] Using module filter: ${moduleFilter}`);
      
      // Fetch only the latest revision with alias support
      const { data: latestRevision } = await supabase
        .from('revisions')
        .select('id')
        .eq('audit_id', id)
        .or(moduleFilter)
        .order('created_at', { ascending: false })
        .limit(1)
        .single()

      if (!latestRevision) {
        setTaxAccountMetrics(null)
        return
      }

      const { data: aggregate } = await supabase
        .from('aggregates')
        .select('summary_json')
        .eq('revision_id', latestRevision.id)
        .single()

      if (!aggregate?.summary_json) {
        setTaxAccountMetrics(null)
        return
      }

      let negativeBalanceDays = 0
      let totalDeviationAmount = 0
      let latePayments = 0

      const findings = (aggregate.summary_json as any)?.findings || []
      findings.forEach((f: any) => {
        const message = (f.shortMessage || f.message || '').toLowerCase()
        const category = (f.category || '').toLowerCase()
        
        if (message.includes('negativt saldo') || category.includes('negativt saldo')) {
          negativeBalanceDays++
          if (f.amount) totalDeviationAmount += Math.abs(f.amount)
        }
        if (message.includes('sen betalning') || message.includes('försenad') || category.includes('sen')) {
          latePayments++
        }
      })

      setTaxAccountMetrics({
        negativeBalanceDays,
        totalDeviationAmount,
        latePayments
      })
    } catch (err) {
      console.error('Error fetching tax account metrics:', err)
      setTaxAccountMetrics(null)
    }
  }

  // Function to delete a document
  const deleteDocument = async (documentId: string) => {
    try {
      // Get document details first
      const docToDelete = documents.find(d => d.id === documentId)
      if (!docToDelete) return

      // Get current user for soft delete tracking
      const { data: { user } } = await supabase.auth.getUser();

      // Soft delete: Mark as deleted instead of removing
      // File stays in storage and can be recovered for 30 days
      const { error: dbError } = await supabase
        .from('audit_documents')
        .update({ 
          deleted_at: new Date().toISOString(),
          deleted_by: user?.id || null
        })
        .eq('id', documentId)

      if (dbError) throw dbError

      // Update local state
      const updatedDocuments = documents.filter(d => d.id !== documentId)
      setDocuments(updatedDocuments)
      recalculateModuleStates(updatedDocuments, moduleSignoffs)

      toast({
        title: 'Dokument raderat',
        description: `${docToDelete.original_filename} kan återställas i 30 dagar`,
      })
    } catch (error) {
      console.error('Delete error:', error)
      toast({
        title: 'Fel vid radering',
        description: error instanceof Error ? error.message : 'Ett fel uppstod',
        variant: 'destructive',
      })
    }
  }

  const fetchCaseData = async () => {
    try {
      // Fetch audit
      const { data: auditData, error: auditError } = await supabase
        .from('audits')
        .select('*')
        .eq('id', id)
        .single()

      if (auditError) throw auditError
      setAudit(auditData)
      setIsOwner(auditData.user_id === profile?.id || role === 'system_admin')
      
      // Load materiality thresholds from data_input if they exist
      const dataInput = auditData.data_input as Record<string, unknown> | null;
      if (dataInput?.thresholds) {
        setMaterialityThresholds(dataInput.thresholds as MaterialityThresholds)
      }

      // Fetch audit documents from the unified system (exclude soft-deleted)
      const { data: documentsData, error: documentsError } = await supabase
        .from('audit_documents')
        .select('*')
        .eq('audit_id', id)
        .is('deleted_at', null)

      // Fetch module signoffs
      const { data: moduleSignoffsData, error: moduleSignoffsError } = await supabase
        .from('module_signoffs')
        .select('*')
        .eq('audit_id', id)

      if (!moduleSignoffsError && moduleSignoffsData) {
        setModuleSignoffs(moduleSignoffsData)
      }

      if (!documentsError && documentsData) {
        console.log('📄 Documents fetched:', documentsData);
        console.log('📄 Documents by module:', documentsData.reduce((acc, doc) => {
          acc[doc.module_type] = (acc[doc.module_type] || 0) + 1;
          return acc;
        }, {} as Record<string, number>));
        
        // Will be set after injecting SIE files
        let allDocuments: Document[] = [...documentsData];
        
        // Convert documents to engagementFiles format (keyed by file_key)
        const filesMap: Record<FileKey, EngagementFile | undefined> = {} as Record<FileKey, EngagementFile | undefined>;
        documentsData.forEach((doc: any) => {
          if (doc.file_key) {
            // Only keep the latest file for each file_key
            const existing = filesMap[doc.file_key as FileKey];
            if (!existing || new Date(doc.created_at) > new Date(existing.uploadedAt)) {
              filesMap[doc.file_key as FileKey] = {
                id: doc.id,
                fileKey: doc.file_key as FileKey,
                fileName: doc.filename,
                originalFileName: doc.original_filename,
                storagePath: doc.storage_path,
                mimeType: doc.mime_type,
                sizeBytes: doc.size_bytes,
                uploadedAt: doc.created_at,
                uploadedBy: doc.uploaded_by,
                status: 'uploaded',
              };
            }
          }
        });
        
        // Inject SIE files from sie_master_data into engagementFiles
        const { data: sieData } = await supabase
          .from('sie_master_data')
          .select('id, fiscal_year_index, company_name, total_vouchers, created_at')
          .eq('audit_id', id);

        if (sieData && sieData.length > 0) {
          // Current year SIE (fiscal_year_index = 0) → GL_SIE
          const currentYear = sieData.find(s => s.fiscal_year_index === 0);
          if (currentYear) {
            filesMap['GL_SIE'] = {
              id: currentYear.id,
              fileKey: 'GL_SIE',
              fileName: `${currentYear.company_name || 'Huvudbok'} - SIE`,
              originalFileName: `SIE-fil (${currentYear.total_vouchers || 0} verifikationer)`,
              storagePath: '',
              mimeType: 'application/x-sie',
              sizeBytes: 0,
              uploadedAt: currentYear.created_at,
              uploadedBy: undefined,
              status: 'uploaded',
            };
            // Add to allDocuments for "Alla dokument" view
            allDocuments.push({
              id: `sie-current-${currentYear.id}`,
              filename: 'SIE-current.se',
              original_filename: `${currentYear.company_name || 'Huvudbok'} - Innevarande år (${currentYear.total_vouchers || 0} verifikationer)`,
              size_bytes: 0,
              created_at: currentYear.created_at,
              module_type: 'data_quality',
              storage_path: '',
              mime_type: 'application/x-sie',
              uploaded_by: '',
              audit_id: id,
              updated_at: currentYear.created_at,
              file_key: 'GL_SIE',
            });
          }
          
          // Previous year SIE (fiscal_year_index = -1) → GL_SIE_PREV
          const prevYear = sieData.find(s => s.fiscal_year_index === -1);
          if (prevYear) {
            filesMap['GL_SIE_PREV'] = {
              id: prevYear.id,
              fileKey: 'GL_SIE_PREV',
              fileName: `${prevYear.company_name || 'Huvudbok'} - SIE föregående år`,
              originalFileName: `SIE-fil (${prevYear.total_vouchers || 0} verifikationer)`,
              storagePath: '',
              mimeType: 'application/x-sie',
              sizeBytes: 0,
              uploadedAt: prevYear.created_at,
              uploadedBy: undefined,
              status: 'uploaded',
            };
            // Add to allDocuments for "Alla dokument" view
            allDocuments.push({
              id: `sie-prev-${prevYear.id}`,
              filename: 'SIE-prev.se',
              original_filename: `${prevYear.company_name || 'Huvudbok'} - Föregående år (${prevYear.total_vouchers || 0} verifikationer)`,
              size_bytes: 0,
              created_at: prevYear.created_at,
              module_type: 'data_quality',
              storage_path: '',
              mime_type: 'application/x-sie',
              uploaded_by: '',
              audit_id: id,
              updated_at: prevYear.created_at,
              file_key: 'GL_SIE_PREV',
            });
          }
          console.log('📄 SIE files injected into engagementFiles:', { currentYear: !!currentYear, prevYear: !!prevYear });
        }
        
        // Set documents including SIE files
        setDocuments(allDocuments);
        setEngagementFiles(filesMap);
        console.log('📄 EngagementFiles populated:', filesMap);
        
        // Recalculate module states with the fresh document data
        recalculateModuleStates(documentsData, moduleSignoffsData || [])

        // Fetch the latest revision per module to filter findings
        const { data: revisionsData } = await supabase
          .from('revisions')
          .select('id, module_type, created_at')
          .eq('audit_id', id)
          .eq('status', 'completed')
          .order('created_at', { ascending: false })

        // Get latest revision ID per module
        const latestByModule: Record<string, string> = {}
        revisionsData?.forEach(rev => {
          if (!latestByModule[rev.module_type]) {
            latestByModule[rev.module_type] = rev.id
          }
        })
        const latestRevisionIds = Object.values(latestByModule)

        // Fetch analysis findings - only from latest revision per module
        let findingsQuery = supabase
          .from('audit_findings')
          .select('id, severity, finding_type, description, created_at, data_reference, module_id, run_id')
          .eq('audit_id', id)
          .order('created_at', { ascending: false })

        // If we have latest revisions, filter to only those
        if (latestRevisionIds.length > 0) {
          findingsQuery = findingsQuery.in('run_id', latestRevisionIds)
        }

        const { data: findingsData, error: findingsError } = await findingsQuery

        if (!findingsError && findingsData) {
          // Store findings from latest revisions only
          setRawFindings(findingsData)
          console.log('📊 Findings loaded (latest per module):', {
            totalFindings: findingsData.length,
            latestRevisions: latestRevisionIds.length,
            moduleTypes: Object.keys(latestByModule)
          })
        }
      }

      // Fetch audit members
      const { data: membersData, error: membersError } = await supabase
        .from('audit_members')
        .select(`
          id,
          user_id,
          role,
          added_at,
          profiles:user_id (
            full_name,
            email,
            avatar_url
          )
        `)
        .eq('audit_id', id)

      if (!membersError && membersData) {
        const auditMembers: AuditMember[] = membersData.map(member => {
          const profileData = Array.isArray(member.profiles) ? member.profiles[0] : member.profiles
          return {
            id: member.id,
            user_id: member.user_id,
            role: member.role,
            added_at: member.added_at,
            full_name: profileData?.full_name,
            email: profileData?.email,
            avatar_url: profileData?.avatar_url
          }
        })

        // Add owner if not already in members
        if (!auditMembers.some(m => m.user_id === auditData.user_id)) {
          auditMembers.unshift({
            id: `owner_${auditData.user_id}`,
            user_id: auditData.user_id,
            role: 'owner',
            added_at: auditData.created_at,
            full_name: profile?.full_name
          })
        }

        setMembers(auditMembers)
      }

      // Fetch client upload tokens with document counts
      const { data: tokensData, error: tokensError } = await supabase
        .from('client_upload_tokens')
        .select('id, client_name, client_email, requested_file_keys, expires_at, created_at, revoked_at, access_count')
        .eq('audit_id', id)
        .order('created_at', { ascending: false });

      if (!tokensError && tokensData) {
        // Fetch document counts per token
        const { data: docCounts } = await supabase
          .from('audit_documents')
          .select('upload_token_id')
          .eq('audit_id', id)
          .not('upload_token_id', 'is', null);

        // Count documents per token
        const countMap: Record<string, number> = {};
        (docCounts || []).forEach((doc: { upload_token_id: string | null }) => {
          if (doc.upload_token_id) {
            countMap[doc.upload_token_id] = (countMap[doc.upload_token_id] || 0) + 1;
          }
        });

        // Add upload_count to each token
        const tokensWithCounts = tokensData.map(token => ({
          ...token,
          upload_count: countMap[token.id] || 0
        }));

        setClientUploadTokens(tokensWithCounts as ClientUploadToken[]);
      }

      // Initialize empty arrays for other data
      setAnalyses([])
      setComments([])
      setActionItems([])
      setFindings([])
      setFindingComments({})
      setFindingDecisions([])

    } catch (error) {
      console.error('Error fetching case data:', error)
      toast({
        title: "Fel",
        description: "Kunde inte hämta ärendedata",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const addComment = async () => {
    if (!newComment.trim()) return

    const comment: Comment = {
      id: `comment_${Date.now()}`,
      content: newComment,
      author: profile?.full_name || 'Okänd användare',
      created_at: new Date().toISOString()
    }

    setComments(prev => [...prev, comment])
    setNewComment('')
    
    toast({
      title: "Kommentar tillagd",
      description: "Din kommentar har sparats"
    })
  }

  // Function to sign off a module
  const signOffModule = async (moduleId: string, comment: string = '') => {
    try {
      const { data, error } = await supabase
        .from('module_signoffs')
        .insert({
          audit_id: id,
          module_id: moduleId,
          signed_off_by: profile?.id,
          comment: comment || null
        })
        .select()
        .single()

      if (error) throw error

      // Update local state
      const newSignoff: ModuleSignoff = {
        id: data.id,
        audit_id: id!,
        module_id: moduleId,
        signed_off_by: profile?.id!,
        signed_off_at: data.signed_off_at,
        comment: comment || undefined
      }

      const updatedSignoffs = [...moduleSignoffs, newSignoff]
      setModuleSignoffs(updatedSignoffs)
      
      // Recalculate module states with updated signoffs
      recalculateModuleStates(documents, updatedSignoffs)

      toast({
        title: "Modul avslutad",
        description: `${modules.find(m => m.id === moduleId) ? t(modules.find(m => m.id === moduleId)!.nameKey) : moduleId} har markerats som klar`,
      })
    } catch (error) {
      console.error('Signoff error:', error)
      toast({
        title: 'Fel vid avslut',
        description: error instanceof Error ? error.message : 'Ett fel uppstod',
        variant: 'destructive',
      })
    }
  }

  // Function to remove module signoff
  const removeModuleSignoff = async (moduleId: string) => {
    try {
      const signoffToRemove = moduleSignoffs.find(s => s.module_id === moduleId)
      if (!signoffToRemove) return

      const { error } = await supabase
        .from('module_signoffs')
        .delete()
        .eq('id', signoffToRemove.id)

      if (error) throw error

      // Update local state
      const updatedSignoffs = moduleSignoffs.filter(s => s.module_id !== moduleId)
      setModuleSignoffs(updatedSignoffs)
      
      // Recalculate module states with updated signoffs
      recalculateModuleStates(documents, updatedSignoffs)

      toast({
        title: "Modulavslut borttaget",
        description: `Avslut för ${modules.find(m => m.id === moduleId) ? t(modules.find(m => m.id === moduleId)!.nameKey) : moduleId} har tagits bort`,
      })
    } catch (error) {
      console.error('Remove signoff error:', error)
      toast({
        title: 'Fel vid borttagning',
        description: error instanceof Error ? error.message : 'Ett fel uppstod',
        variant: 'destructive',
      })
    }
  }

  // Function to save materiality thresholds
  const saveMaterialityThresholds = async () => {
    if (!id || !audit) return;
    
    setIsSavingThresholds(true);
    try {
      const currentDataInput = (audit.data_input as Record<string, unknown>) || {};
      const updatedDataInput = {
        ...currentDataInput,
        thresholds: materialityThresholds as unknown
      };
      
      const { error } = await supabase
        .from('audits')
        .update({ 
          data_input: updatedDataInput as unknown as Json,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);
      
      if (error) throw error;
      
      // Update local audit state
      setAudit({ ...audit, data_input: updatedDataInput as any });
      
      toast({
        title: 'Trösklar sparade',
        description: 'Revisionströsklar har sparats och kommer användas i framtida analyser',
      });
    } catch (error) {
      console.error('Error saving thresholds:', error);
      toast({
        title: 'Fel vid sparning',
        description: error instanceof Error ? error.message : 'Kunde inte spara trösklar',
        variant: 'destructive',
      });
    } finally {
      setIsSavingThresholds(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    if (bytes === 0) return '0 Byte'
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)).toString())
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
  }

  // Removed - using formatWithUTC from @/lib/datetime

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!audit) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Ärende kunde inte hittas</p>
        <Button onClick={() => navigate('/cases')} className="mt-4">
          Tillbaka till ärenden
        </Button>
      </div>
    )
  }

  const renderModuleDetailView = () => {
    if (!selectedModule) return null
    
    const module = getModule(selectedModule)
    const moduleState = moduleStates[selectedModule]
    const moduleDocuments = documents.filter(doc => doc.module_type === selectedModule)
    
    console.log('🎯 Module detail view:', {
      selectedModule,
      moduleState,
      totalDocuments: documents.length,
      moduleDocuments: moduleDocuments.length,
      allDocs: documents.map(d => ({ id: d.id, module_type: d.module_type, filename: d.original_filename }))
    });

    return (
      <div className="space-y-6">
        {/* Module Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => handleModuleSelect(null)}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Tillbaka till moduler
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold">{module ? t(module.nameKey) : ''}</h2>
              <Badge className={moduleStatusColors[moduleState.status]} variant="outline">
                {moduleStatusLabels[moduleState.status]}
              </Badge>
            </div>
          </div>
        </div>

        {/* Module Tabs - 5 Standard Tabs */}
        <Tabs value={selectedModuleTab} onValueChange={setSelectedModuleTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">{t('modules.tabs.overview')}</TabsTrigger>
            <TabsTrigger value="files">{t('modules.tabs.files')}</TabsTrigger>
            <TabsTrigger value="run">{t('modules.tabs.run')}</TabsTrigger>
            <TabsTrigger value="results">{t('modules.tabs.results')}</TabsTrigger>
            <TabsTrigger value="actions">{t('modules.tabs.report')}</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <ModuleOverviewTab
              module={module!}
              moduleState={{
                moduleId: selectedModule || '',
                status: moduleState.status === 'in_progress' ? 'running' : moduleState.status === 'completed' ? 'completed' : 'not_started',
                selected: true,
                filesProgress: {
                  required: module?.requiredFiles.length || 0,
                  requiredUploaded: module?.requiredFiles.filter(fk => engagementFiles[fk]).length || 0,
                  optional: module?.optionalFiles.length || 0,
                  optionalUploaded: module?.optionalFiles.filter(fk => engagementFiles[fk]).length || 0,
                },
                lastRunAt: moduleStats?.lastAnalysisDate || undefined,
                findingsCount: moduleStats?.totalFindings,
              }}
              dataGateStatus={dataGateStatus}
              stats={moduleStats || undefined}
              signoff={moduleSignoffs.find(s => s.module_id === selectedModule) ? {
                signed_off_by: moduleSignoffs.find(s => s.module_id === selectedModule)!.signed_off_by,
                signed_off_at: moduleSignoffs.find(s => s.module_id === selectedModule)!.signed_off_at,
                comment: moduleSignoffs.find(s => s.module_id === selectedModule)!.comment || undefined,
              } : undefined}
              onSignOff={() => signOffModule(selectedModule || '')}
              onRemoveSignOff={() => removeModuleSignoff(selectedModule || '')}
              userDisplayName={profile?.full_name || undefined}
            />
          </TabsContent>

          {/* Files Tab */}
          <TabsContent value="files">
            <ModuleFilesTab
              module={module!}
              auditId={id || ''}
              engagementFiles={engagementFiles}
              onFileUploaded={(fileKey, file) => {
                setEngagementFiles(prev => ({ ...prev, [fileKey]: file }));
              }}
              onRefresh={fetchCaseData}
            />
          </TabsContent>

          {/* Run Tab */}
          <TabsContent value="run">
            <ModuleRunTab
              module={module!}
              auditId={id || ''}
              dataGateStatus={dataGateStatus}
              engagementFiles={engagementFiles}
              isRunning={isTaxAccountModule ? backendWorker.isRunning : isRunningAnalysis}
              lastRunAt={moduleStats?.lastAnalysisDate || undefined}
              useBackendWorker={isTaxAccountModule}
              batchStats={isTaxAccountModule ? backendWorker.batchStats : null}
              progress={isTaxAccountModule ? backendWorker.progress : 0}
              workerError={isTaxAccountModule ? backendWorker.error : null}
              onCancelAnalysis={isTaxAccountModule ? backendWorker.cancelAnalysis : undefined}
              showTaxAccountConfig={isTaxAccountModule}
              toleranceDays={toleranceDays}
              onToleranceDaysChange={setToleranceDays}
              companySize={companySize}
              onCompanySizeChange={setCompanySize}
              vatReportingPeriod={vatReportingPeriod}
              onVatReportingPeriodChange={setVatReportingPeriod}
              onStartAnalysis={async () => {
                const currentModule = getModule(selectedModule || '');
                if (!currentModule) {
                  toast({
                    title: 'Modul hittades inte',
                    variant: 'destructive',
                  });
                  return;
                }
                
                console.log('[CaseDetailPage] Starting analysis for module:', selectedModule);
                
                // Get the uploaded files for this module
                const moduleDocuments = documents.filter(doc => doc.module_type === selectedModule);
                
                if (moduleDocuments.length === 0) {
                  toast({
                    title: 'Inga filer uppladdade',
                    description: 'Ladda upp filer i Filer-fliken först',
                    variant: 'destructive',
                  });
                  return;
                }
                
                // Use backend worker for tax account module
                if (isTaxAccountModule) {
                  // Collect ALL files for the module (multi-file support)
                  const moduleFilePaths = moduleDocuments
                    .filter(doc => doc.storage_path)
                    .map(doc => doc.storage_path);
                  
                  if (moduleFilePaths.length === 0) {
                    toast({
                      title: 'Inga filer hittades',
                      description: 'Uppladdade filer saknar lagringssökväg',
                      variant: 'destructive',
                    });
                    return;
                  }
                  
                  // Use first file for display name, but send all for analysis
                  const primaryFile = moduleDocuments[0];
                  const fileCountInfo = moduleFilePaths.length > 1 
                    ? ` (${moduleFilePaths.length} filer)` 
                    : '';
                  
                  console.log('[CaseDetailPage] Starting backend worker analysis with files:', moduleFilePaths);
                  
                  // Fetch saved field mapping from database (check all module aliases)
                  const moduleAliases = getModuleAliases(selectedModule)
                  const moduleNameFilter = moduleAliases.map(a => `module_name.eq.${a}`).join(',')
                  
                  const { data: savedMapping, error: mappingError } = await supabase
                    .from('field_mappings')
                    .select('mapping, dialect')
                    .eq('customer_id', id)
                    .or(moduleNameFilter)
                    .order('updated_at', { ascending: false })
                    .limit(1)
                    .maybeSingle();
                  
                  if (mappingError) {
                    console.error('[CaseDetailPage] Error fetching field mapping:', mappingError);
                  }
                  
                  let fieldMapping: Record<string, string>;
                  
                  if (savedMapping?.mapping && Object.keys(savedMapping.mapping as Record<string, string>).length > 0) {
                    // Use saved mapping from database (already in canonical format: CSV-header → standardField)
                    fieldMapping = savedMapping.mapping as Record<string, string>;
                    console.log('[CaseDetailPage] Using saved field mapping from database:', fieldMapping);
                  } else {
                    // No saved mapping - show warning and require user to map fields first
                    toast({
                      title: 'Fältmappning saknas',
                      description: 'Gå till Filer-fliken och mappa CSV-fälten innan analys kan startas',
                      variant: 'destructive',
                    });
                    setSelectedModuleTab('files');
                    return;
                  }
                  
                  await backendWorker.startAnalysis({
                    storagePaths: moduleFilePaths,  // Multi-file array
                    fieldMapping: fieldMapping,
                    companySize: companySize,
                    vatReportingPeriod: vatReportingPeriod,
                    toleranceDays: toleranceDays,
                    fileName: `${primaryFile.original_filename}${fileCountInfo}`,
                    companyName: audit?.title || undefined,
                    orgNumber: undefined,
                    thresholds: materialityThresholds ? {
                      clearly_trivial_threshold: materialityThresholds.clearly_trivial_threshold?.value,
                      overall_materiality: materialityThresholds.overall_materiality?.value,
                      performance_materiality: materialityThresholds.performance_materiality?.value,
                    } : undefined,
                  });
                } else {
                  // Use legacy edge function flow for other modules
                  setIsRunningAnalysis(true);
                  try {
                    const fileIds = moduleDocuments.map(doc => doc.id);
                    const edgeFunctionName = currentModule.edgeFunctionName || 'audit-tax-account-analyze';
                    
                    console.log('[CaseDetailPage] Invoking edge function:', edgeFunctionName, 'with fileIds:', fileIds);
                    
                    const { data, error } = await supabase.functions.invoke(edgeFunctionName, {
                      body: {
                        auditId: id,
                        fileIds: fileIds,
                        companyConfig: {
                          toleranceDays: toleranceDays,
                        },
                      },
                    });
                    
                    if (error) {
                      console.error('[CaseDetailPage] Analysis error:', error);
                      throw error;
                    }
                    
                    console.log('[CaseDetailPage] Analysis complete:', data);
                    
                    toast({
                      title: 'Analys slutförd',
                      description: `Hittade ${data?.findings?.length || 0} observationer`,
                    });
                    
                    await fetchCaseData();
                    await fetchModuleStats();
                    await fetchLatestFindings();
                    setSelectedModuleTab('results');
                  } catch (error) {
                    console.error('[CaseDetailPage] Analysis failed:', error);
                    toast({
                      title: 'Analys misslyckades',
                      description: error instanceof Error ? error.message : 'Ett fel uppstod',
                      variant: 'destructive',
                    });
                  } finally {
                    setIsRunningAnalysis(false);
                  }
                }
              }}
              onNavigateToFiles={() => setSelectedModuleTab('files')}
            />
          </TabsContent>

          {/* Results Tab */}
          <TabsContent value="results">
            <ModuleResultsTab
              auditId={id || ''}
              moduleId={selectedModule || ''}
              findingSummary={moduleStats ? {
                total: moduleStats.totalFindings,
                critical: moduleStats.criticalFindings,
                high: moduleStats.highFindings,
                medium: moduleStats.mediumFindings,
                low: moduleStats.lowFindings,
              } : undefined}
              hasRuns={(moduleStats?.analysesRun || 0) > 0}
              onNavigateToRun={() => setSelectedModuleTab('run')}
              onGenerateReport={handleGenerateReport}
              onNavigateToReport={() => setSelectedModuleTab('actions')}
              isGeneratingReport={isGeneratingReport}
              hasExistingReport={hasExistingReport}
            />
          </TabsContent>

          {/* Report Tab */}
          <TabsContent value="actions">
            <ModuleReportTab
              auditId={id || ''}
              moduleId={selectedModule || ''}
              findings={getFilteredFindings(moduleFindingsExtended).map(f => ({
                id: f.id,
                finding_type: f.findingType,
                severity: f.severity,
                description: f.description,
                created_at: f.createdAt,
                data_reference: {
                  details: f.recommendedNextStep,
                  recommendation: f.recommendedEvidence,
                }
              }))}
              hasCompletedAnalysis={(moduleStats?.analysesRun || 0) > 0}
              onNavigateToRun={() => setSelectedModuleTab('run')}
              onReportGenerated={() => {
                setHasExistingReport(true);
              }}
            />
          </TabsContent>
        </Tabs>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Show Module Detail View or Main Case View */}
      {selectedModule ? renderModuleDetailView() : (
        <>
          {/* Header */}
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/cases')}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Tillbaka
            </Button>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="gap-2 text-3xl font-bold tracking-tight h-auto p-1 hover:bg-muted/50">
                      {audit.title}
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-72 bg-white z-50">
                    <DropdownMenuLabel className="text-xs text-muted-foreground">Aktiva ärenden</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {activeCases.length === 0 ? (
                      <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                        Inga aktiva ärenden
                      </div>
                    ) : (
                      activeCases.map(c => (
                        <DropdownMenuItem 
                          key={c.id}
                          onClick={() => navigate(`/cases/${c.id}`)}
                          className={`cursor-pointer ${c.id === id ? "bg-accent" : ""}`}
                        >
                          <FolderOpen className="mr-2 h-4 w-4 text-muted-foreground" />
                          <span className="truncate">{c.title}</span>
                          {c.id === id && (
                            <span className="ml-auto text-xs text-muted-foreground">Aktuellt</span>
                          )}
                        </DropdownMenuItem>
                      ))
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Badge className={statusColors[audit.status]}>
                  {audit.status}
                </Badge>
              </div>
              <p className="text-muted-foreground mt-1 ml-1">{audit.description}</p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => navigate(`/final-report/${id}`)}
                className="gap-2"
              >
                <FileText className="h-4 w-4" />
                Slutrapport
              </Button>
              <Button className="gap-2">
                <Download className="h-4 w-4" />
                Exportera data
              </Button>
            </div>
          </div>
          
          {/* Client Upload Link Modal */}
          <CreateClientUploadLinkModal
            open={showClientUploadModal}
            onOpenChange={setShowClientUploadModal}
            auditId={id || ''}
            auditName={audit.title}
          />

          {/* Module Navigation */}
          <div className="border rounded-lg p-4 bg-card">
            <h2 className="text-lg font-semibold mb-4">Moduler</h2>
            
            {/* Module 0 - Data Gate (Full Width) */}
            {modules.filter(m => m.displayOrder === 0).map((module) => {
              const state = moduleStates[module.id]
              const signoff = moduleSignoffs.find(s => s.module_id === module.id)
              return (
                <Card 
                  key={module.id}
                  className={`cursor-pointer transition-all hover:shadow-md mb-4 bg-muted/30 border-muted ${selectedModule === module.id ? 'ring-2 ring-primary' : ''}`}
                  onClick={() => handleModuleSelect(selectedModule === module.id ? null : module.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold">
                          {module.displayOrder}
                        </span>
                        <h3 className="font-medium text-sm">{t(module.nameKey)}</h3>
                      </div>
                      <Badge className={moduleStatusColors[state.status]} variant="outline">
                        {moduleStatusLabels[state.status]}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mb-2">
                      {state.documentsUploaded} dokument uppladdade
                    </div>
                    {state.documentsUploaded > 0 && (
                      <Progress 
                        value={state.status === 'completed' ? 100 : 75} 
                        className="h-1 mb-2" 
                      />
                    )}
                    {state.status === 'completed' && signoff && (
                      <div className="text-xs text-muted-foreground">
                        Avslutad {formatWithUTC(signoff.signed_off_at)}
                      </div>
                    )}
                    {state.status === 'in_progress' && (
                      <div className="flex gap-1 mt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-xs"
                          onClick={(e) => {
                            e.stopPropagation()
                            signOffModule(module.id)
                          }}
                        >
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Avsluta
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )
            })}

            {/* SIE Accounting Basis - Full Width, after Data Gate */}
            <SieAccountingBasisCard 
              auditId={id || ''}
              className="mb-4"
            />

            {/* Other Modules (3-column grid) - excludes Datakvalitet (0) and Redovisningsgrund (1) which have dedicated cards above */}
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {modules.filter(m => m.displayOrder !== 0 && m.displayOrder !== 1).map((module) => {
                const state = moduleStates[module.id]
                const signoff = moduleSignoffs.find(s => s.module_id === module.id)
                return (
                  <Card 
                    key={module.id}
                    className={`cursor-pointer transition-all hover:shadow-md ${selectedModule === module.id ? 'ring-2 ring-primary' : ''}`}
                    onClick={() => handleModuleSelect(selectedModule === module.id ? null : module.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium text-sm">{t(module.nameKey)}</h3>
                        <Badge className={moduleStatusColors[state.status]} variant="outline">
                          {moduleStatusLabels[state.status]}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mb-2">
                        {state.documentsUploaded} dokument uppladdade
                      </div>
                      {state.documentsUploaded > 0 && (
                        <Progress 
                          value={state.status === 'completed' ? 100 : 75} 
                          className="h-1 mb-2" 
                        />
                      )}
                      {state.status === 'completed' && signoff && (
                        <div className="text-xs text-muted-foreground">
                          Avslutad {formatWithUTC(signoff.signed_off_at)}
                        </div>
                      )}
                      {state.status === 'in_progress' && (
                        <div className="flex gap-1 mt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 px-2 text-xs"
                            onClick={(e) => {
                              e.stopPropagation()
                              signOffModule(module.id)
                            }}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Avsluta
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          </div>

          {/* Overview Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Dokument</CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{documents.length}</div>
                <p className="text-xs text-muted-foreground">
                  totalt uppladdade
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Analyser</CardTitle>
                <Brain className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analysisCount}</div>
                <div className="flex gap-3 mt-1 text-xs">
                  <span className="text-red-600">Hög: {analysisSeverity.high}</span>
                  <span className="text-yellow-600">Medel: {analysisSeverity.medium}</span>
                  <span className="text-green-600">Låg: {analysisSeverity.low}</span>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Action Items</CardTitle>
                <AlertCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{actionItems.length}</div>
                <p className="text-xs text-muted-foreground">
                  {actionItems.filter(a => a.status === 'open').length} öppna
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Kommentarer</CardTitle>
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{comments.length}</div>
                <p className="text-xs text-muted-foreground">
                  totalt
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Main Tabs */}
          <Tabs defaultValue="documents" className="space-y-6">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="documents">Dokument</TabsTrigger>
              <TabsTrigger value="analyses">Analyser</TabsTrigger>
              <TabsTrigger value="actions">Action Items</TabsTrigger>
              <TabsTrigger value="comments">Kommentarer</TabsTrigger>
              <TabsTrigger value="members">Medlemmar</TabsTrigger>
              <TabsTrigger value="settings" className="gap-1">
                <Settings className="h-4 w-4" />
                Inställningar
              </TabsTrigger>
            </TabsList>

            {/* Documents Tab */}
            <TabsContent value="documents">
              <Card>
                <CardHeader>
                  <CardTitle>Alla dokument</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {documents.length > 0 ? (
                      documents.map((doc) => {
                        const isSieFile = doc.mime_type === 'application/x-sie';
                        return (
                          <div key={doc.id} className="border rounded-lg p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <FileText className="h-4 w-4" />
                                  <h4 className="font-medium">{doc.original_filename}</h4>
                                  {isSieFile && (
                                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                      SIE-fil
                                    </Badge>
                                  )}
                                  {doc.module_type && (
                                    <Badge variant="outline" className="text-xs">
                                      {(() => {
                                        const moduleObj = modules.find(m => m.id === doc.module_type)
                                        return moduleObj ? t(moduleObj.nameKey) : doc.module_type
                                      })()}
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {isSieFile ? 'Processad SIE-data' : formatFileSize(doc.size_bytes)} • {formatWithUTC(doc.created_at)}
                                </div>
                              </div>
                              <div className="flex gap-2">
                                {!isSieFile && (
                                  <>
                                    <Button size="sm" variant="outline" className="gap-2">
                                      <Eye className="h-4 w-4" />
                                      Visa
                                    </Button>
                                    <Button size="sm" variant="outline" className="gap-2">
                                      <Download className="h-4 w-4" />
                                      Ladda ner
                                    </Button>
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <Button 
                                          size="sm" 
                                          variant="outline" 
                                          className="gap-2 text-destructive hover:text-destructive-foreground hover:bg-destructive"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                          Radera
                                        </Button>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent>
                                        <AlertDialogHeader>
                                          <AlertDialogTitle>Radera dokument</AlertDialogTitle>
                                          <AlertDialogDescription>
                                            Är du säker på att du vill radera "{doc.original_filename}"? 
                                            Filen kan återställas inom 30 dagar.
                                          </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel>Avbryt</AlertDialogCancel>
                                          <AlertDialogAction 
                                            onClick={() => deleteDocument(doc.id)}
                                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                          >
                                            Radera
                                          </AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  </>
                                )}
                                {isSieFile && (
                                  <Button size="sm" variant="outline" className="gap-2" asChild>
                                    <a href={`/cases/${audit?.id}/redovisningsgrund`}>
                                      <Eye className="h-4 w-4" />
                                      Visa i Redovisningsgrund
                                    </a>
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        Inga dokument uppladdade
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {/* Deleted files recovery panel */}
              {id && (
                <div className="mt-4">
                  <DeletedFilesPanel auditId={id} onFileRestored={fetchCaseData} />
                </div>
              )}
            </TabsContent>

            {/* Analyses Tab */}
            <TabsContent value="analyses">
              <Card>
                <CardHeader>
                  <CardTitle>Analyser</CardTitle>
                </CardHeader>
                <CardContent>
                  {auditFindings.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      Inga analyser har körts än. Välj en modul för att starta analyser.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {auditFindings.map((f) => (
                        <div key={f.id} className="p-3 border rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline">
                              {f.severity === 'critical' ? 'Kritisk' : f.severity === 'high' ? 'Hög' : f.severity === 'medium' ? 'Medel' : 'Låg'}
                            </Badge>
                            <Badge variant="secondary" className="text-xs">{f.finding_type}</Badge>
                            {f.data_reference?.source_file && (
                              <Badge variant="outline" className="text-xs">📄 {f.data_reference.source_file}</Badge>
                            )}
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                              {(() => {
                                const moduleObj = modules.find(m => m.id === f.module_id)
                                return moduleObj ? t(moduleObj.nameKey) : 'Okänd modul'
                              })()}
                            </Badge>
                          </div>
                          <div className="text-sm font-medium">{f.description}</div>
                          <div className="text-xs text-muted-foreground">{formatWithUTC(f.created_at)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Action Items Tab */}
            <TabsContent value="actions">
              <Card>
                <CardHeader>
                  <CardTitle>Action Items</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8 text-muted-foreground">
                    Inga action items har skapats än
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Comments Tab */}
            <TabsContent value="comments">
              <Card>
                <CardHeader>
                  <CardTitle>Kommentarer</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Add Comment */}
                  <div className="space-y-2">
                    <Label htmlFor="comment">Lägg till kommentar</Label>
                    <Textarea
                      id="comment"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Skriv en kommentar..."
                      className="min-h-[80px]"
                    />
                    <div className="flex justify-end">
                      <Button onClick={addComment} disabled={!newComment.trim()}>
                        Lägg till kommentar
                      </Button>
                    </div>
                  </div>

                  {/* Comments List */}
                  <div className="space-y-3">
                    {comments.map((comment) => (
                      <div key={comment.id} className="p-3 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium text-sm">{comment.author}</div>
                          <div className="text-xs text-muted-foreground">
                            {formatWithUTC(comment.created_at)}
                          </div>
                        </div>
                        <p className="text-sm">{comment.content}</p>
                      </div>
                    ))}
                    {comments.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        Inga kommentarer än
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Members Tab */}
            <TabsContent value="members">
              <MembersManager
                auditId={id}
                members={members}
                onMembersChange={setMembers}
                currentUserId={profile?.id || ''}
                isOwner={isOwner}
                organizationId={organization?.id}
              />
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings" className="space-y-6">
              {/* Client Upload Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Link className="h-5 w-5" />
                    Kunduppladdning
                  </CardTitle>
                  <CardDescription>
                    Skapa säkra engångslänkar för kunduppladdning. Länkarna visas endast vid skapandet 
                    och kan inte hämtas igen – skapa en ny länk varje gång du behöver skicka till kunden.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button 
                    onClick={() => setShowClientUploadModal(true)}
                    className="gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Skapa ny kundlänk
                  </Button>
                  
                  <ClientUploadLinksTable 
                    tokens={clientUploadTokens}
                    onRefresh={fetchCaseData}
                  />
                </CardContent>
              </Card>

              {/* Materiality Thresholds */}
              <MaterialityThresholdsPanel
                thresholds={materialityThresholds}
                onThresholdsChange={setMaterialityThresholds}
                onSave={saveMaterialityThresholds}
                isSaving={isSavingThresholds}
              />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  )
}